import {
	FIR_SIZE,
	type FiveInRowState,
	NIM_MAX_TAKE,
	type NimState,
	type PlayerIndex,
	type ReflexState,
	type RpsState,
	THROW_EMOJI,
	THROWS,
} from "@playpal/core";
import { useEffect, useState } from "react";
import { Button } from "../components/ui";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
	clockOffset?: number;
}

// --- Nim Sticks -----------------------------------------------------------

export function NimBoard({ state, seat, onMove }: BoardProps<NimState>) {
	const myTurn = state.turn === seat && !state.result;

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-4 text-lg font-semibold">
				{state.sticks} stick{state.sticks === 1 ? "" : "s"} left
				{state.sticks === 1 && " — whoever takes it loses!"}
			</p>

			<div
				className="mb-6 flex flex-wrap justify-center gap-2"
				role="img"
				aria-label={`${state.sticks} sticks remaining`}
			>
				{Array.from({ length: state.sticks }, (_, i) => (
					<span
						key={i}
						className="h-16 w-3 rounded-full bg-amber-600 shadow-md sm:h-20 sm:w-4 dark:bg-amber-500"
					/>
				))}
			</div>

			<div className="flex justify-center gap-3">
				{Array.from({ length: NIM_MAX_TAKE }, (_, i) => i + 1).map((take) => (
					<Button
						key={take}
						size="lg"
						disabled={!myTurn || take > state.sticks}
						onClick={() => onMove({ take })}
					>
						Take {take}
					</Button>
				))}
			</div>
		</div>
	);
}

// --- Five in a Row --------------------------------------------------------

const FIR_MARK = ["●", "✕"] as const;

export function FiveInRowBoard({ state, seat, onMove }: BoardProps<FiveInRowState>) {
	const myTurn = state.turn === seat && !state.result;

	return (
		<div
			className="board-frame board-lg grid aspect-square gap-0.5 rounded-2xl bg-amber-200/70 p-1.5 shadow-xl sm:gap-1 dark:bg-amber-900/30"
			style={{ gridTemplateColumns: `repeat(${FIR_SIZE}, minmax(0, 1fr))` }}
		>
			{state.board.map((cell, index) => {
				const winning = state.winningLine?.includes(index) ?? false;
				return (
					<button
						key={index}
						type="button"
						disabled={!myTurn || cell !== null}
						aria-label={`Row ${Math.floor(index / FIR_SIZE) + 1}, column ${(index % FIR_SIZE) + 1}${
							cell === null ? ", empty" : ""
						}`}
						onClick={() => onMove({ square: index })}
						className={`flex items-center justify-center rounded-md text-lg font-black transition sm:text-2xl ${
							winning ? "bg-amber-300" : "bg-white/70 dark:bg-white/10"
						} ${cell === 0 ? "text-rose-500" : cell === 1 ? "text-sky-500" : ""} ${
							index === state.lastMove ? "ring-2 ring-indigo-500" : ""
						}`}
					>
						{cell === null ? "" : FIR_MARK[cell]}
					</button>
				);
			})}
		</div>
	);
}

// --- Rock Paper Scissors Plus ---------------------------------------------

export function RpsBoard({ state, seat, onMove }: BoardProps<RpsState>) {
	const myPick = state.picks[seat];
	const theirPick = state.picks[seat === 0 ? 1 : 0];
	const revealing = state.phase === "reveal";

	return (
		<div className="board-frame board-xl text-center">
			<p className="mb-4 text-lg font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				Round {state.round + 1} of 5 · {state.scores[0]} – {state.scores[1]}
			</p>

			{revealing ? (
				<div className="mb-6">
					<p className="text-7xl" aria-hidden="true">
						{myPick !== null && myPick >= 0 ? THROW_EMOJI[THROWS[myPick]] : "🤷"}
						<span className="mx-3 text-3xl align-middle">vs</span>
						{theirPick !== null && theirPick >= 0 ? THROW_EMOJI[THROWS[theirPick]] : "🤷"}
					</p>
					<p className="mt-3 text-2xl font-black">
						{state.lastRound?.winner === null
							? "It's a tie!"
							: state.lastRound?.winner === seat
								? "You win the round!"
								: "They win the round!"}
					</p>
					<p className="text-lg font-semibold opacity-80">{state.lastRound?.because}</p>
				</div>
			) : (
				<div className="mb-6 grid grid-cols-3 gap-3 sm:grid-cols-5">
					{THROWS.map((name, index) => (
						<button
							key={name}
							type="button"
							disabled={myPick !== null}
							aria-label={name}
							onClick={() => onMove({ pick: index })}
							className={`rounded-2xl px-2 py-4 shadow-md transition ${
								myPick === index
									? "bg-indigo-600 text-white"
									: "bg-white/80 enabled:hover:scale-105 dark:bg-white/10"
							}`}
						>
							<span className="block text-4xl" aria-hidden="true">
								{THROW_EMOJI[name]}
							</span>
							<span className="mt-1 block text-sm font-bold">{name}</span>
						</button>
					))}
				</div>
			)}

			<p className="text-lg font-semibold" aria-live="polite">
				{revealing
					? "Next round coming up…"
					: myPick !== null
						? theirPick !== null
							? "Both in!"
							: "Locked in — waiting for your friend…"
						: "Choose your throw!"}
			</p>
		</div>
	);
}

// --- Reflex Tap -----------------------------------------------------------

export function ReflexBoard({ state, seat, onMove, clockOffset = 0 }: BoardProps<ReflexState>) {
	const myTap = state.taps[seat];
	const green = state.phase === "go";
	const revealing = state.phase === "reveal";

	// Nudges a redraw so the light changes the instant the deadline passes.
	const [, setNow] = useState(0);
	useEffect(() => {
		if (revealing) return;
		const timer = setInterval(() => setNow(Date.now() + clockOffset), 100);
		return () => clearInterval(timer);
	}, [revealing, clockOffset]);

	// A false start is checked before the green light: a player who jumped is
	// out of the round, and the light turning green must not dress their -1
	// up as a reaction time.
	const background = revealing
		? "bg-indigo-500"
		: myTap === -1
			? "bg-slate-500"
			: green
				? "bg-emerald-500"
				: "bg-rose-600";

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-3 text-lg font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				Round {state.round + 1} of 5 · {state.scores[0]} – {state.scores[1]}
			</p>

			<button
				type="button"
				disabled={revealing || myTap !== null}
				onClick={() => onMove({ tap: true })}
				aria-label={green ? "Tap now!" : "Wait for green"}
				className={`flex h-64 w-full items-center justify-center rounded-3xl text-4xl font-black text-white shadow-xl transition ${background}`}
			>
				{revealing
					? (state.lastRound?.because ?? "")
					: myTap === -1
						? "Too early…"
						: green
							? myTap !== null
								? `${myTap}ms`
								: "TAP!"
							: "Wait…"}
			</button>

			<p className="mt-4 text-lg font-semibold">
				{state.best[seat] !== null
					? `Your best: ${state.best[seat]}ms`
					: "Wait for green, then tap"}
			</p>
		</div>
	);
}
